import { Link } from 'react-router-dom'
import { Star, MapPin, Calendar, Shield, Users, Ship, ChevronLeft, ChevronRight } from 'lucide-react'
import { useFranchiseData } from '../hooks/useFranchiseData'
import { usePackages } from '../hooks/usePackages'
import { getPackageDestination } from '../utils/packageHelpers'

const features = [
  {
    icon: Ship,
    title: 'Luxury Cruise Lines',
    description: 'Premium cruise lines with world-class amenities'
  },
  {
    icon: Shield,
    title: 'All-Inclusive Packages',
    description: 'Meals, entertainment, and activities included'
  },
  {
    icon: MapPin,
    title: 'Shore Excursions',
    description: 'Curated land experiences at each port'
  },
  {
    icon: Users,
    title: 'Family-Friendly',
    description: 'Activities and facilities for all ages'
  }
]

export default function Cruises() {
  const { franchiseData, loading: franchiseLoading } = useFranchiseData()
  const {
    packages,
    pagination,
    loading: packagesLoading,
    error,
    loadPage,
    goToNextPage,
    goToPrevPage,
  } = usePackages({ initialFilters: { offer_type: 18, per_page: 12 }, initialPage: 1 })

  const loading = franchiseLoading || packagesLoading

  const getImageUrl = (pkg: any) => {
    if (pkg.cover_photo?.large_url) return pkg.cover_photo.large_url
    if (pkg.cover_photo?.url) return pkg.cover_photo.url
    if (pkg.scraped_cover_photo) return pkg.scraped_cover_photo
    return null
  }

  if (loading && !franchiseData) {
    return (
      <div className="bg-white min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-center">
          <div className="h-8 bg-gray-300 rounded w-64 mx-auto mb-4"></div>
          <div className="h-4 bg-gray-300 rounded w-48 mx-auto"></div>
        </div>
      </div>
    )
  }
  
  if (error) {
    return (
      <div className="bg-white py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">Something went wrong</h2>
            <p className="mt-6 text-lg leading-8 text-red-600">{error}</p>
            <button onClick={() => window.location.reload()} className="mt-8 btn-primary">Try Again</button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white">
      {/* Hero Section */}
      <div className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url('/stars4.jpg')`
          }}
        />
        
        <div className="relative z-10 max-w-4xl mx-auto text-center px-6">
          <h1 className="text-5xl lg:text-6xl font-extrabold text-white mb-6">
            Cruise Adventures
          </h1>
          <p className="text-xl text-white mb-8 max-w-2xl mx-auto">
            Discover the world's most beautiful destinations by water. From luxury ocean liners to intimate river cruises, we offer unforgettable maritime adventures.
          </p>
        </div>
      </div>

      {/* Features Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Why Choose Our Cruise Packages?
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We partner with the world's leading cruise lines to offer exceptional experiences
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-purple-600 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cruise Packages (offer_type=18) */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Cruise Packages</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">Handpicked cruise adventures filtered for you</p>
          </div>

          {/* Loading indicator while we already have some packages */}
          {loading && packages.length > 0 && (
            <div className="mx-auto mt-10 text-center">
              <div className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-600">
                <div className="animate-spin h-4 w-4 border-2 border-purple-600 border-t-transparent rounded-full mr-2"></div>
                Loading...
              </div>
            </div>
          )}

          {/* Packages Grid */}
          <div className="mx-auto mt-2 grid max-w-2xl grid-cols-1 gap-x-8 gap-y-20 lg:mx-0 lg:max-w-none lg:grid-cols-3">
            {packages.map((pkg) => {
              const imageUrl = getImageUrl(pkg)
              const destination = getPackageDestination(pkg)
              return (
                <article key={pkg.id} className="flex flex-col items-start group">
                  <Link to={`/packages/${pkg.slug}`} className="w-full">
                    <div className="relative w-full">
                      <div
                        className="aspect-[16/9] w-full rounded-2xl object-cover sm:aspect-[2/1] lg:aspect-[3/2] flex items-center justify-center group-hover:shadow-lg transition-shadow duration-300"
                        style={{
                          backgroundColor: franchiseData?.accent_color || '#F896C7',
                          backgroundImage: imageUrl ? `url(${imageUrl})` : undefined,
                          backgroundSize: 'cover',
                          backgroundPosition: 'center'
                        }}
                      >
                        {!imageUrl && <Ship className="h-12 w-12 text-white opacity-60" />}
                      </div>
                      {pkg.category && (
                        <div className="absolute top-4 left-4 bg-white px-3 py-1 rounded-full text-sm font-medium">
                          {pkg.category}
                        </div>
                      )}
                      <div className="absolute top-4 right-4 bg-purple-600 text-white px-3 py-1 rounded-full text-sm font-medium">
                        {pkg.nights} {pkg.nights === 1 ? 'Night' : 'Nights'}
                      </div>
                    </div>
                  </Link>

                  <div className="max-w-xl w-full">
                    <div className="mt-8 flex items-center gap-x-4 text-xs">
                      <div className="flex items-center gap-1">
                        {Array.from({ length: pkg.rating || 5 }).map((_, i) => (
                          <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                        ))}
                      </div>
                      <span className="text-gray-500 flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {destination}
                      </span>
                      <span className="text-gray-500 flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {pkg.duration}
                      </span>
                    </div>
                    <div className="group relative">
                      <Link to={`/packages/${pkg.slug}`}>
                        <h3 className="mt-3 text-lg font-semibold leading-6 text-gray-900 group-hover:text-gray-600 transition-colors">
                          {pkg.name}
                        </h3>
                      </Link>
                      <p className="mt-5 line-clamp-3 text-sm leading-6 text-gray-600">
                        {pkg.intro || pkg.description}
                      </p>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {pkg.includes?.map((item: string) => (
                        <span
                          key={item}
                          className="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium"
                          style={{
                            backgroundColor: `${franchiseData?.accent_color || '#F896C7'}20`,
                            color: franchiseData?.button_color || '#F464AC'
                          }}
                        >
                          {item}
                        </span>
                      ))}
                    </div>
                    <div className="mt-4 flex items-center gap-x-4">
                      <div className="flex items-center gap-x-2">
                        <Calendar className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-600">{pkg.dates}</span>
                      </div>
                    </div>
                    <div className="mt-6 flex items-center justify-between">
                      <div className="flex flex-col">
                        <span className="text-2xl font-bold text-purple-600">From {pkg.formatted_price} pps</span>
                        <span className="text-xs text-gray-500">{pkg.total_pax}</span>
                      </div>
                      <Link to={`/packages/${pkg.slug}`} className="btn-primary">
                        View Details
                      </Link>
                    </div>
                  </div>
                </article>
              )
            })}
          </div>

          {/* No results */}
          {packages.length === 0 && !loading && (
            <div className="mx-auto mt-16 max-w-2xl text-center">
              <h3 className="text-lg font-semibold text-gray-900">No cruise packages found</h3>
              <p className="mt-2 text-sm text-gray-600">Please check back later.</p>
            </div>
          )}

          {/* Pagination */}
          {pagination.total_pages > 1 && (
            <div className="mx-auto mt-20 flex items-center justify-center gap-4">
              <button
                onClick={goToPrevPage}
                disabled={!pagination.has_prev}
                className="flex items-center px-4 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </button>

              <div className="flex items-center gap-2">
                {Array.from({ length: Math.min(pagination.total_pages, 7) }, (_, i) => {
                  let page
                  if (pagination.total_pages <= 7) {
                    page = i + 1
                  } else {
                    const current = pagination.current_page
                    if (current <= 4) {
                      page = i + 1
                    } else if (current >= pagination.total_pages - 3) {
                      page = pagination.total_pages - 6 + i
                    } else {
                      page = current - 3 + i
                    }
                  }

                  return (
                    <button
                      key={page}
                      onClick={() => loadPage(page as number)}
                      className={`px-3 py-2 text-sm font-medium rounded-md ${
                        page === pagination.current_page ? 'bg-purple-600 text-white' : 'text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {page as number}
                    </button>
                  )
                })}
              </div>

              <button
                onClick={goToNextPage}
                disabled={!pagination.has_next}
                className="flex items-center px-4 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </button>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-r from-purple-600 to-blue-600">
        <div className="max-w-4xl mx-auto text-center px-6">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Set Sail?
          </h2>
          <p className="text-xl text-white mb-8">
            Let us help you find the perfect cruise with personalized service and expert recommendations.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-white text-purple-600 font-semibold px-8 py-3 rounded-lg hover:bg-gray-100 transition">
              Get Your Quote
            </button>
            <button className="bg-transparent border border-white text-white font-semibold px-8 py-3 rounded-lg hover:bg-white hover:text-purple-600 transition">
              Contact Us
            </button>
          </div>
        </div>
      </section>
    </div>
  )
}
